import React, { useCallback, useEffect, useState } from "react";
import { Layout, List, Card, Input, Button, Avatar, Spin, message } from "antd";
import { SendOutlined } from "@ant-design/icons";
import moment from "moment";
import { useLoaderData } from "react-router-dom";
import { apiClient } from "~/services/apiClient";
import { INTENTS } from "~/utils";
import type {
  MessageContact,
  MessagePageLoaderType,
} from "./loaders/MessagePageLoader";

const { Sider, Content } = Layout;

interface ChatMessage {
  id: string;
  senderEmail: string;
  content: string;
  timestamp: Date;
}

const MessagesPage = () => {
  const loaderData = useLoaderData() as MessagePageLoaderType;
  const contacts = loaderData.contacts;

  const [selectedContact, setSelectedContact] = useState<MessageContact | null>(
    null,
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const loadThread = useCallback(async (contact: MessageContact) => {
    setIsLoadingThread(true);
    try {
      const result = await apiClient.loader<{
        messages: {
          id: string;
          senderEmail: string;
          content: string;
          timestamp: string;
        }[];
      }>("messages-thread", { contactEmail: contact.email });

      if (!result.success || !result.data) {
        message.error(result.error ?? "Failed to load messages");
        setMessages([]);
        return;
      }

      setMessages(
        result.data.messages.map((m) => ({
          id: m.id,
          senderEmail: m.senderEmail,
          content: m.content,
          timestamp: new Date(m.timestamp),
        })),
      );
    } finally {
      setIsLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedContact) {
      setMessages([]);
      return;
    }

    void loadThread(selectedContact);
    const interval = window.setInterval(() => {
      void loadThread(selectedContact);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [selectedContact, loadThread]);

  const handleSendMessage = async () => {
    if (newMessage.trim() === "" || !selectedContact) return;

    setIsSending(true);
    try {
      const result = await apiClient.sendIntent<{ id: string }>(
        INTENTS._send_chat_message,
        {
          contactEmail: selectedContact.email,
          content: newMessage,
        },
      );

      if (!result.success) {
        message.error(result.error ?? "Failed to send message");
        return;
      }

      setNewMessage("");
      await loadThread(selectedContact);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Layout style={{ minHeight: "80vh", backgroundColor: "#f0f2f5" }}>
      <Sider
        width="33%"
        style={{ backgroundColor: "white", padding: "10px" }}
        data-tour="messages-contacts"
      >
        <List
          dataSource={contacts}
          renderItem={(contact: MessageContact) => (
            <List.Item
              onClick={() => setSelectedContact(contact)}
              style={{
                cursor: "pointer",
                backgroundColor:
                  selectedContact?.email === contact.email ? "#e6f7ff" : "",
              }}
            >
              <List.Item.Meta
                avatar={<Avatar src={contact.profilePictureUrl} />}
                title={contact.name}
              />
            </List.Item>
          )}
        />
      </Sider>

      <Content style={{ padding: "20px" }}>
        {selectedContact ? (
          <Card
            data-tour="messages-thread"
            title={`Conversation with ${selectedContact.name}`}
            style={{ height: "100%", position: "relative" }}
          >
            {isLoadingThread && messages.length === 0 ? (
              <Spin style={{ display: "block", margin: "24px auto" }} />
            ) : (
              <List
                dataSource={messages}
                renderItem={(msg: ChatMessage) => (
                  <List.Item>
                    <List.Item.Meta
                      title={msg.senderEmail}
                      description={msg.content}
                    />
                    <div>{moment(msg.timestamp).calendar()}</div>
                  </List.Item>
                )}
                style={{ maxHeight: "600px", overflowY: "auto" }}
              />
            )}
            <div
              data-tour="messages-composer"
              style={{
                position: "absolute",
                bottom: 20,
                width: "calc(100% - 40px)",
              }}
            >
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                style={{ width: "calc(100% - 60px)", marginRight: "10px" }}
                onPressEnter={() => void handleSendMessage()}
                disabled={isSending}
              />
              <Button
                type="primary"
                onClick={() => void handleSendMessage()}
                icon={<SendOutlined />}
                loading={isSending}
              />
            </div>
          </Card>
        ) : (
          <div>Please select a contact to view messages.</div>
        )}
      </Content>
    </Layout>
  );
};

export default MessagesPage;
